import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ArrowLeft, MapPin, Mail, Phone, Globe, ShieldCheck, ShieldAlert, FileSpreadsheet, Star, Clock, MessageSquare, User, ExternalLink } from "lucide-react";

export default function ContactDetailPage() {
    const { id } = useParams();
    const q = useQuery({
        queryKey: ["contact", id],
        queryFn: () => api.get(`/contacts/${id}`).then((r) => r.data),
    });

    if (q.isLoading) return <div className="text-textmuted">Caricamento…</div>;
    if (q.isError || !q.data) return <div className="text-critical">Contatto non trovato.</div>;

    const { contact: c, interactions, tasks } = q.data;
    const displayName = [c.first_name, c.last_name].filter(Boolean).join(" ") || c.full_name || "—";

    return (
        <div className="space-y-6 pv-enter">
            <div className="flex items-center gap-3">
                <Link to="/contacts" className="h-8 w-8 rounded-lg hover:bg-surface2 flex items-center justify-center text-textmuted">
                    <ArrowLeft size={15} strokeWidth={1.5} />
                </Link>
                <div className="pv-eyebrow">Contatti importati / Scheda</div>
            </div>

            <div className="pv-card p-6 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 h-52 w-52 rounded-full bg-primary/[0.06] blur-3xl" />
                <div className="relative flex items-start gap-5 flex-wrap">
                    <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary text-xl font-medium">
                        {(displayName[0] || "?").toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="pv-serif text-3xl leading-tight tracking-tight">{displayName}</h1>
                            {c.ker_id && <span className="text-[11px] pv-mono px-2 py-0.5 rounded-md bg-primary/[0.08] text-primary">{c.ker_id}</span>}
                            {c.contact_state && <span className="text-[11px] px-2 py-0.5 rounded-md bg-textsubtle/10 text-textmuted">{c.contact_state}</span>}
                            {c.priority && <span className="text-[11px] px-2 py-0.5 rounded-md bg-warning/10 text-warning">Priorità {c.priority}</span>}
                            {c.score != null && <span className="text-[11px] pv-mono text-textmuted">Score {c.score}</span>}
                        </div>
                        <div className="text-[13px] text-textmuted mt-0.5">
                            {[c.role, c.specialization, c.regime].filter(Boolean).join(" · ") || "Nessuna qualifica registrata"}
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-[12px] text-textmuted flex-wrap">
                            {c.city && <Info icon={MapPin} text={`${c.city}${c.regione ? ", " + c.regione : ""}`} />}
                            {c.email && <Info icon={Mail} text={c.email} />}
                            {c.phone && <Info icon={Phone} text={c.phone} />}
                            {c.profile_link && (
                                <a href={c.profile_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors">
                                    <Globe size={12} strokeWidth={1.5} /> Profilo <ExternalLink size={10} strokeWidth={1.5} />
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                {/* Colonna sinistra: ultimo contatto + ultimo messaggio + consensi */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="pv-card p-5">
                        <div className="pv-eyebrow mb-2 flex items-center gap-1.5"><Clock size={11} strokeWidth={1.5} />Ultimo contatto</div>
                        {c.last_contact_at ? (
                            <>
                                <div className="text-[15px] font-medium">
                                    {new Date(c.last_contact_at).toLocaleString("it-IT")}
                                </div>
                                <div className="text-[13px] text-textmuted mt-1">
                                    {c.last_contact_outcome || <span className="text-textsubtle">Nessun esito registrato</span>}
                                </div>
                            </>
                        ) : (
                            <div className="text-[13px] text-textsubtle">Nessuna data di ultimo contatto presente nel file.</div>
                        )}
                        {c.next_followup_at && (
                            <div className="mt-3 pt-3 border-t border-border/[var(--border-opacity)] text-[12.5px]">
                                <span className="text-textsubtle">Prossimo follow-up:</span>{" "}
                                <b>{new Date(c.next_followup_at).toLocaleString("it-IT")}</b>
                            </div>
                        )}
                    </div>

                    <div className="pv-card p-5">
                        <div className="pv-eyebrow mb-2 flex items-center gap-1.5"><MessageSquare size={11} strokeWidth={1.5} />Ultimo messaggio inviato</div>
                        {c.last_message_text ? (
                            <div className="text-[13px] text-textmain leading-relaxed">{c.last_message_text}</div>
                        ) : (
                            <div className="flex items-start gap-2 text-[13px] text-warning">
                                <ShieldAlert size={13} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="font-medium">Non presente nel file</div>
                                    <div className="text-[12px] text-textmuted mt-0.5">
                                        Il workbook Excel non contiene una colonna con il testo dell'ultimo messaggio inviato. Non è stato inventato nulla.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="pv-card p-5">
                        <div className="pv-eyebrow mb-3 flex items-center gap-1.5"><Clock size={11} strokeWidth={1.5} />Timeline interazioni ({interactions.length})</div>
                        {interactions.length === 0 ? (
                            <div className="text-[12.5px] text-textsubtle">Nessuna interazione registrata.</div>
                        ) : (
                            <ol className="space-y-3 relative border-l border-border/[var(--border-opacity)] pl-5">
                                {interactions.map((e) => (
                                    <li key={e.id} className="relative">
                                        <div className={`absolute -left-[26px] top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-background ${
                                            e.certainty === "high" ? "bg-primary" : "bg-warning/70"
                                        }`} />
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="text-[13px]">{e.label}</div>
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-md uppercase tracking-wider ${
                                                e.certainty === "high" ? "bg-primary/[0.08] text-primary" : "bg-warning/10 text-warning"
                                            }`}>certezza {e.certainty}</span>
                                        </div>
                                        <div className="text-[11px] text-textsubtle mt-0.5">
                                            {new Date(e.at).toLocaleString("it-IT")} · <span className="pv-mono">{e.source}</span>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        )}
                    </div>

                    {tasks?.length > 0 && (
                        <div className="pv-card p-5">
                            <div className="pv-eyebrow mb-3">Task Sales collegati ({tasks.length})</div>
                            <div className="space-y-2">
                                {tasks.map((t) => (
                                    <div key={t.id} className="pv-card p-3 pv-hover">
                                        <div className="text-[13px] font-medium">{t.title || "—"}</div>
                                        <div className="text-[11px] text-textsubtle mt-0.5 pv-mono">{t.source}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Colonna destra: consensi + provenance + note */}
                <div className="space-y-4">
                    <div className="pv-card p-5">
                        <div className="pv-eyebrow mb-3 flex items-center gap-1.5"><ShieldCheck size={11} strokeWidth={1.5} />Consenso & canale</div>
                        <div className="space-y-2.5 text-[12.5px]">
                            <ConsentRow label="Consenso marketing" v={c.marketing_consent} />
                            <ConsentRow label="Newsletter" v={c.newsletter_consent} />
                            <ConsentRow label="WhatsApp proattivo" v={c.whatsapp_proactive} />
                            <ConsentRow label="Chiamato?" v={c.called} />
                            <ConsentRow label="Contattato?" v={c.contacted} />
                            <div className="pt-2 border-t border-border/[var(--border-opacity)]">
                                <div className="pv-eyebrow mb-1.5">Canali consentiti</div>
                                {c.allowed_channels?.length ? (
                                    <div className="flex flex-wrap gap-1">
                                        {c.allowed_channels.map((ch, i) => (
                                            <span key={i} className="text-[11px] px-2 py-0.5 rounded-md bg-success/10 text-success">{ch}</span>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-[11.5px] text-textsubtle">Nessun canale specificato</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="pv-card p-5">
                        <div className="pv-eyebrow mb-3 flex items-center gap-1.5"><FileSpreadsheet size={11} strokeWidth={1.5} />Provenance dato</div>
                        {c.source_provenance ? (
                            <ProvenanceBlock p={c.source_provenance} primary />
                        ) : (
                            <div className="text-[12px] text-textsubtle">Provenance non registrata</div>
                        )}
                        {c.merged_from?.length > 1 && (
                            <div className="mt-4 pt-3 border-t border-border/[var(--border-opacity)]">
                                <div className="pv-eyebrow mb-2">Fonti unite ({c.merged_from.length})</div>
                                <div className="space-y-2">
                                    {c.merged_from.slice(1).map((p, i) => <ProvenanceBlock key={i} p={p} />)}
                                </div>
                            </div>
                        )}
                    </div>

                    {(c.notes || c.environment_need || c.next_action) && (
                        <div className="pv-card p-5">
                            <div className="pv-eyebrow mb-2">Note & bisogni</div>
                            {c.next_action && (
                                <div className="mb-3">
                                    <div className="text-[10.5px] uppercase tracking-wider text-textsubtle">Prossima azione</div>
                                    <div className="text-[13px] mt-0.5">{c.next_action}</div>
                                </div>
                            )}
                            {c.environment_need && (
                                <div className="mb-3">
                                    <div className="text-[10.5px] uppercase tracking-wider text-textsubtle">Ambiente / bisogno</div>
                                    <div className="text-[13px] mt-0.5">{c.environment_need}</div>
                                </div>
                            )}
                            {c.notes && (
                                <div>
                                    <div className="text-[10.5px] uppercase tracking-wider text-textsubtle">Note</div>
                                    <div className="text-[13px] mt-0.5 whitespace-pre-wrap">{c.notes}</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Info({ icon: I, text }) {
    return (
        <span className="inline-flex items-center gap-1.5 truncate max-w-full">
            <I size={12} strokeWidth={1.5} />{text}
        </span>
    );
}

function ConsentRow({ label, v }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-textmuted">{label}</span>
            {v === true && <span className="text-success text-[11px] font-medium">Sì</span>}
            {v === false && <span className="text-critical text-[11px] font-medium">No</span>}
            {(v === null || v === undefined) && <span className="text-textsubtle text-[11px]">non presente nel file</span>}
        </div>
    );
}

function ProvenanceBlock({ p, primary }) {
    return (
        <div className={`p-2.5 rounded-lg ${primary ? "bg-primary/[0.04] border border-primary/10" : "bg-surface2"} text-[11.5px] space-y-1`}>
            <div className="pv-mono text-textmain truncate">📄 {p.filename}</div>
            <div className="text-textmuted">
                Foglio <b>{p.sheet}</b> · riga <b>{p.excel_row}</b>
            </div>
            {p.crm_origine && <div className="text-textmuted">CRM origine: <b className="text-textmain">{p.crm_origine}</b></div>}
            {p.sheet_origine && <div className="text-textmuted">Sheet origine: <b className="text-textmain">{p.sheet_origine}</b></div>}
            {p.original_source && <div className="text-textmuted">Fonte originale: <b className="text-textmain">{p.original_source}</b></div>}
            {p.fonti_unite && <div className="text-textmuted">Fonti unite: <b className="text-textmain">{p.fonti_unite}</b></div>}
        </div>
    );
}
